import React, { useState, useEffect, useContext, createContext } from "react";
import ApplicationPreferencesApi from "../api/Admin/ApplicationPreference";
import { ApplicationPreferences } from "../api/types";
import { useAuth } from "./AuthContext";

interface ModalContent {
  title: string;
  message: string;
}

const MODAL_CONTENT_INITIAL_STATE: ModalContent = {
  title: "",
  message: "",
};

const defaultPreferences: ApplicationPreferences = {
  userEmail: null,
  receiveEmailNotifications: false,
  receiveDailyUpdate: false,
  showWorkRequestWidget: false,
  showRecentSamplesWidget: false,
  showRecentTestsWidget: false,
  showRecentBlendsWidget: false,
  isSampleRetainMetadata: false,
  lastLogin: null,
  homeSiteName: null,
  homeSite: null,
  memberProjects: null,
};

interface PreferencesContextType {
  userPreferences: ApplicationPreferences;
  setUserPreferences: React.Dispatch<React.SetStateAction<ApplicationPreferences>>;
  updateUserPreferences: () => Promise<void>;
  isLoading: boolean;
  modalContent: ModalContent;
  setModalContent: React.Dispatch<React.SetStateAction<ModalContent>>;
}

const PrefContext = createContext<PreferencesContextType | null>(null);

interface PreferencesContextProviderProps {
  children: React.ReactNode;
}

export const PrefContextProvider = ({ children }: PreferencesContextProviderProps) => {
  const [userPreferences, setUserPreferences] =
    useState<ApplicationPreferences>(defaultPreferences);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<ModalContent>(MODAL_CONTENT_INITIAL_STATE);
  const { identity } = useAuth();

  const updateUserPreferences = async () => {
    setIsLoading(true);
    try {
      await ApplicationPreferencesApi.update(identity?.username, userPreferences);
      setModalContent({ title: "Success!", message: "Preferences updated successfully!" });
    } catch (err) {
      setModalContent({
        title: "Error!",
        message: "There was an error updating your preferences. Please, try again later!",
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    const getUserPreferences = async () => {
      setIsLoading(true);
      const appPref = await ApplicationPreferencesApi.getMyApplicationPreferences();
      const copiedPreferences = JSON.parse(JSON.stringify(appPref));
      setUserPreferences(copiedPreferences);
      setIsLoading(false);
    };

    getUserPreferences();
  }, [identity?.username]);

  return (
    <PrefContext.Provider
      value={{
        userPreferences,
        setUserPreferences,
        updateUserPreferences,
        isLoading,
        modalContent,
        setModalContent,
      }}>
      {children}
    </PrefContext.Provider>
  );
};

export const usePreferences = (): PreferencesContextType => {
  const context = useContext(PrefContext);
  if (!context) {
    throw new Error("usePreferences must be used within a PrefContextProvider");
  }
  return context;
};

export default PrefContext; //remove eventually & replace with usePreferences hook
